import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { CURRENCIES, curOf, fmt, fmtIDR, fmtNum, typeColor } from "../lib/format";
import Keypad, { applyKey } from "../components/Keypad";
import { ArrowRight, Loader2, CheckCircle2, Plus, AlertTriangle, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ProductSale from "../components/ProductSale";

export default function NewTransaction() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState("exchange");
  const [rates, setRates] = useState([]);
  const [gives, setGives] = useState("USDT");
  const [receives, setReceives] = useState("IDR");
  const [givesAmt, setGivesAmt] = useState("");
  const [rate, setRate] = useState("");
  const [recvAmt, setRecvAmt] = useState("");
  const [nnxCost, setNnxCost] = useState("");
  const [exNnxRate, setExNnxRate] = useState("");
  const [exMktRate, setExMktRate] = useState("");
  const [rateEdited, setRateEdited] = useState(false);
  const [fee, setFee] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { api.get("/rates").then((r) => setRates(r.data)).catch(() => {}); }, []);

  const rateMap = useMemo(() => Object.fromEntries(rates.map((r) => [r.currency, r])), [rates]);
  const ttype = gives !== "IDR" && receives === "IDR" ? "BUY" : gives === "IDR" && receives !== "IDR" ? "SELL" : "EXCHANGE";
  const tc = typeColor(ttype);

  // EXCHANGE orientation: quote is always "weaker per 1 stronger" (IDR value from rate board).
  // Give the stronger currency -> multiply; give the weaker -> divide.
  const exCur = useMemo(() => {
    const vG = Number(rateMap[gives]?.sell_rate || 0);
    const vR = Number(rateMap[receives]?.sell_rate || 0);
    const giveIsStronger = vG >= vR;
    return { giveIsStronger, strong: giveIsStronger ? gives : receives, weak: giveIsStronger ? receives : gives };
  }, [rateMap, gives, receives]);

  // autofill rate from rate board
  useEffect(() => {
    if (rateEdited || ttype === "EXCHANGE") return;
    if (ttype === "BUY" && rateMap[gives]) setRate(rateMap[gives].buy_rate);
    if (ttype === "SELL" && rateMap[receives]) setRate(rateMap[receives].sell_rate);
  }, [gives, receives, ttype, rateMap, rateEdited]);

  // compute receives amount
  useEffect(() => {
    if (ttype === "EXCHANGE") return;
    const g = Number(givesAmt || 0), r = Number(rate || 0);
    if (!g || !r) { setRecvAmt(""); return; }
    setRecvAmt(ttype === "BUY" ? String(g * r) : String(g / r));
  }, [givesAmt, rate, ttype]);

  // EXCHANGE: derive amounts from NNX rate & market rate (direction-aware)
  useEffect(() => {
    if (ttype !== "EXCHANGE") return;
    const g = Number(givesAmt || 0);
    const conv = (r) => {
      const rr = Number(r || 0);
      if (!g || !rr) return "";
      return String(exCur.giveIsStronger ? g * rr : g / rr);
    };
    setRecvAmt(conv(exNnxRate));
    setNnxCost(conv(exMktRate));
  }, [givesAmt, exNnxRate, exMktRate, ttype, exCur]);

  const doPreview = useCallback(async () => {
    if (gives === receives) { setPreview(null); return; }
    if (!Number(givesAmt) || !Number(recvAmt)) { setPreview(null); return; }
    try {
      const r = await api.post("/transactions/preview", {
        gives_asset: gives, gives_amount: givesAmt, receives_asset: receives,
        receives_amount: recvAmt, fee_amount: fee || "0", nnx_cost_amount: nnxCost || null,
      });
      setPreview(r.data);
    } catch { setPreview(null); }
  }, [gives, receives, givesAmt, recvAmt, fee, nnxCost]);

  useEffect(() => { const t = setTimeout(doPreview, 350); return () => clearTimeout(t); }, [doPreview]);

  const confirm = async () => {
    setLoading(true);
    try {
      const r = await api.post("/transactions", {
        gives_asset: gives, gives_amount: givesAmt, receives_asset: receives,
        receives_amount: recvAmt, fee_amount: fee || "0", fee_currency: "IDR",
        nnx_cost_amount: nnxCost || null,
        customer_name: customerName, customer_note: note, custom_reason: reason,
      });
      setSuccess(r.data);
      if (Number(r.data.net_profit) < 0) toast.warning("Transaksi disimpan (RUGI sesuai rate yang dipilih)");
      else toast.success("Transaksi tersimpan");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const reset = () => {
    setSuccess(null); setGivesAmt(""); setRecvAmt(""); setRate(""); setRateEdited(false);
    setFee(""); setCustomerName(""); setNote(""); setReason(""); setPreview(null); setNnxCost(""); setExNnxRate(""); setExMktRate("");
  };

  const swap = () => {
    const g = gives, r = receives;
    setGives(r); setReceives(g);
    setGivesAmt(""); setRecvAmt(""); setRate(""); setRateEdited(false); setPreview(null); setNnxCost(""); setExNnxRate(""); setExMktRate("");
  };

  if (success) return <SuccessScreen txn={success} onNew={reset} onHome={() => nav("/home")} />;

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="flex gap-2">
        <button data-testid="mode-exchange" onClick={() => setMode("exchange")}
          className={`chip flex-1 py-2.5 font-semibold ${mode === "exchange" ? "bg-[#EAB308] text-[#0B0E14] border-[#EAB308]" : ""}`}>Tukar Uang</button>
        <button data-testid="mode-product" onClick={() => setMode("product")}
          className={`chip flex-1 py-2.5 font-semibold ${mode === "product" ? "bg-violet-500 text-white border-violet-500" : ""}`}>Pulsa & Telegram</button>
      </div>
      {mode === "product" ? (
        <ProductSale onDone={() => nav("/home")} />
      ) : (
      <>
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold">Transaksi Baru</h1>
        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${tc.bg} ${tc.text} ${tc.border}`} data-testid="txn-type-badge">
          {ttype === "EXCHANGE" ? <ArrowLeftRight className="h-3 w-3 inline mr-1" /> : null}{ttype}
        </span>
      </div>

      {/* Customer gives */}
      <div className="nnx-card p-4">
        <div className="label mb-2">Customer Beri (NNX Terima)</div>
        <AssetChips value={gives} onChange={setGives} exclude={receives} />
        <div className="mt-3">
          <input data-testid="gives-amount" inputMode="decimal" value={givesAmt} onChange={(e) => setGivesAmt(e.target.value.replace(/[^0-9.]/g, ""))}
            placeholder="0" className="w-full bg-transparent text-right mono font-bold text-4xl outline-none text-white" />
          <div className="text-right text-xs text-[#64748B] mt-1">{curOf(gives).name}</div>
        </div>
        <div className="mt-3"><Keypad onKey={(k) => setGivesAmt((v) => applyKey(v, k))} /></div>
        <div className="flex gap-2 mt-2">
          {["100", "500", "1000", "5000"].map((p) => (
            <button key={p} className="chip flex-1 mono text-xs hover:border-[#EAB308]" data-testid={`preset-${p}`} onClick={() => setGivesAmt(p)}>{Number(p).toLocaleString()}</button>
          ))}
        </div>
      </div>

      {/* Swap legs */}
      <div className="flex justify-center -my-1">
        <button type="button" data-testid="swap-legs" onClick={swap}
          className="h-10 w-10 rounded-full border border-[#222B3D] bg-[#151B28] flex items-center justify-center hover:border-[#EAB308] hover:text-[#EAB308] transition-colors active:scale-90">
          <ArrowLeftRight className="h-4 w-4 rotate-90" />
        </button>
      </div>

      {/* Customer receives */}
      <div className="nnx-card p-4">
        <div className="label mb-2">Customer Terima (NNX Beri)</div>
        <AssetChips value={receives} onChange={setReceives} exclude={gives} />
        {ttype !== "EXCHANGE" ? (
          <div className="mt-3">
            <label className="label">Rate (IDR per {ttype === "BUY" ? gives : receives})</label>
            <input data-testid="rate-input" inputMode="decimal" value={rate}
              onChange={(e) => { setRate(e.target.value.replace(/[^0-9.]/g, "")); setRateEdited(true); }}
              className="nnx-input mono mt-1 text-lg" placeholder="0" />
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <div>
              <label className="label">Rate NNX → Customer ({exCur.weak}/{exCur.strong})</label>
              <input data-testid="ex-nnx-rate" inputMode="decimal" value={exNnxRate}
                onChange={(e) => setExNnxRate(e.target.value.replace(/[^0-9.]/g, ""))}
                className="nnx-input mono mt-1 text-lg" placeholder={`${exCur.weak} per 1 ${exCur.strong}`} />
            </div>
            <div>
              <label className="label">Rate Pasar → NNX Dapat ({exCur.weak}/{exCur.strong})</label>
              <input data-testid="ex-mkt-rate" inputMode="decimal" value={exMktRate}
                onChange={(e) => setExMktRate(e.target.value.replace(/[^0-9.]/g, ""))}
                className="nnx-input mono mt-1 text-lg" placeholder={`${exCur.weak} per 1 ${exCur.strong}`} />
            </div>
            {recvAmt && (
              <div className="rounded-lg bg-[#0B0E14] border border-[#222B3D] p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[#94A3B8]">Customer Terima</span><span className="mono">{fmt(recvAmt, receives)}</span></div>
                <div className="flex justify-between"><span className="text-[#94A3B8]">NNX Dapat (pasar)</span><span className="mono">{fmt(nnxCost || 0, receives)}</span></div>
                <div className="flex justify-between"><span className="text-[#94A3B8]">Selisih (profit)</span><span className="mono text-emerald-400">{fmt(Math.max(0, Number(nnxCost || 0) - Number(recvAmt || 0)), receives)}</span></div>
              </div>
            )}
          </div>
        )}
        {recvAmt && ttype !== "EXCHANGE" && (
          <div className="mt-3 text-right">
            <div className="text-xs text-[#64748B]">Total {receives}</div>
            <div className="mono font-bold text-2xl text-[#EAB308]">{fmt(recvAmt, receives)}</div>
          </div>
        )}
      </div>

      {/* Optional */}
      <div className="nnx-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="label">Fee (IDR, opsional)</label><input data-testid="fee-input" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value.replace(/[^0-9.]/g, ""))} className="nnx-input mono mt-1" placeholder="0" /></div>
        <div><label className="label">Customer (opsional)</label><input data-testid="customer-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="nnx-input mt-1" placeholder="Nama / catatan" /></div>
      </div>

      {/* Preview */}
      {preview && (
        <div className={`nnx-card p-4 ring-1 ${tc.ring}`} data-testid="txn-preview">
          <div className="label mb-3">Perhitungan Profit</div>
          <Row l="Customer Beri" r={fmt(preview.gives_amount, preview.gives_asset)} />
          <Row l="Customer Terima" r={fmt(preview.receives_amount, preview.receives_asset)} />
          <Row l={`Valuasi ${preview.gives_asset}`} r={fmtIDR(preview.valuation_in)} />
          <Row l={`Valuasi ${preview.receives_asset}`} r={fmtIDR(preview.valuation_out)} />
          <Row l="Nilai Masuk (IDR)" r={fmtIDR(preview.value_in_idr)} />
          <Row l="Nilai Keluar (IDR)" r={fmtIDR(preview.value_out_idr)} />
          {Number(preview.fee_idr) > 0 && <Row l="Fee" r={`- ${fmtIDR(preview.fee_idr)}`} />}
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#222B3D]">
            <span className="text-sm text-[#94A3B8]">Estimated Profit</span>
            <span className={`mono font-bold text-xl ${Number(preview.net_profit) >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="preview-profit">{fmtIDR(preview.net_profit)}</span>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-[#94A3B8]">Margin</span>
            <span className={`mono ${Number(preview.margin_percent) >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="preview-margin">{Number(preview.margin_percent).toFixed(2)}%</span>
          </div>
          {ttype === "EXCHANGE" && (
            <div className="mt-3 rounded-lg bg-[#0B0E14] border border-[#222B3D] p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#94A3B8]">Break Even Rate</span><span className="mono" data-testid="break-even">{Number(preview.break_even_rate).toFixed(4)}</span></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Minimum Safe Rate</span><span className="mono text-[#EAB308]" data-testid="min-safe">{Number(preview.min_safe_rate).toFixed(4)}</span></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Customer Rate</span><span className="mono">{Number(preview.rate).toFixed(4)}</span></div>
            </div>
          )}
          {Number(preview.estimated_profit_idr) >= 0 ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5" data-testid="profitable-badge">
              <CheckCircle2 className="h-4 w-4 shrink-0" /> PROFITABLE
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2.5" data-testid="loss-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" /> RATE CUSTOMER MENYEBABKAN RUGI
            </div>
          )}
          {preview.sufficient === false && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5" data-testid="insufficient-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Catatan: stok {preview.receives_asset} saat ini {fmtNum(preview.outflow_balance, preview.receives_asset)} (akan minus setelah transaksi ini).</span>
            </div>
          )}
          {preview.is_custom_rate && (
            <input data-testid="custom-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="nnx-input mt-3 text-sm" placeholder={`Rate custom (${preview.rate_diff}). Alasan?`} />
          )}
        </div>
      )}

      <button data-testid="confirm-transaction" disabled={loading || !preview} onClick={confirm}
        className="btn-gold w-full text-lg py-3.5 flex items-center justify-center gap-2">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} CONFIRM TRANSACTION
      </button>
      </>
      )}
    </div>
  );
}

function AssetChips({ value, onChange, exclude }) {
  return (
    <div className="flex flex-wrap gap-2">
      {CURRENCIES.map((c) => (
        <button key={c.code} data-testid={`asset-${c.code}`} onClick={() => onChange(c.code)} disabled={c.code === exclude}
          className={`chip whitespace-nowrap ${value === c.code ? "bg-[#EAB308] text-[#0B0E14] border-[#EAB308]" : "bg-[#0B0E14] text-[#94A3B8]"} ${c.code === exclude ? "opacity-30" : ""}`}>
          {c.symbol} {c.code}
        </button>
      ))}
    </div>
  );
}

function Row({ l, r }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-[#94A3B8]">{l}</span><span className="mono">{r}</span></div>;
}

function SuccessScreen({ txn, onNew, onHome }) {
  const tc = typeColor(txn.type);
  return (
    <div className="max-w-md mx-auto text-center pt-6" data-testid="txn-success">
      <div className="h-20 w-20 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: txn.status === "pending_approval" ? "rgba(245,158,11,0.15)" : "rgba(16,185,129,0.15)" }}>
        {txn.status === "pending_approval" ? <AlertTriangle className="h-10 w-10 text-amber-400" /> : <CheckCircle2 className="h-10 w-10 text-emerald-400" />}
      </div>
      <h2 className="font-head text-2xl font-bold">{txn.status === "pending_approval" ? "MENUNGGU APPROVAL" : "TRANSACTION SUCCESS"}</h2>
      <div className="mono text-[#EAB308] mt-1">{txn.txn_code}</div>
      <div className="nnx-card p-5 mt-5 text-left space-y-1">
        <Row l="Tipe" r={<span className={tc.text}>{txn.type}</span>} />
        <Row l="Customer Beri" r={fmt(txn.gives_amount, txn.gives_asset)} />
        <Row l="Customer Terima" r={fmt(txn.receives_amount, txn.receives_asset)} />
        <Row l="Rate" r={fmtNum(txn.rate)} />
        <Row l="Nilai (IDR)" r={fmtIDR(txn.deal_idr)} />
        <Row l="Profit" r={<span className="text-emerald-400">{fmtIDR(txn.net_profit)}</span>} />
        <Row l="Officer" r={txn.officer_name} />
        <Row l="Waktu" r={new Date(txn.created_at).toLocaleTimeString("id-ID")} />
      </div>
      <button data-testid="new-transaction-again" onClick={onNew} className="btn-gold w-full mt-5 text-lg py-3.5 flex items-center justify-center gap-2"><Plus className="h-5 w-5" /> TRANSAKSI BARU</button>
      <button onClick={onHome} className="btn-outline w-full mt-3">Kembali ke POS</button>
    </div>
  );
}
